import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  AlertOctagon,
  AlertTriangle,
  AlertCircle,
  Clock,
  DollarSign,
  Users,
  Frown,
  Lightbulb,
  ArrowRight,
  Target,
} from "lucide-react";

// Severity styling — all on-brand (green), no red. Consistent icon per level.
const IMPACT = {
  "Critical Issue": {
    Icon: AlertOctagon,
    badge: "bg-primary text-white",
    dot: "bg-white",
    bar: "#1b7840",
  },
  "High Impact": {
    Icon: AlertTriangle,
    badge: "bg-primary/10 text-primary",
    dot: "bg-primary",
    bar: "rgba(27,120,64,0.55)",
  },
  "Moderate Impact": {
    Icon: AlertCircle,
    badge: "bg-gray-100 text-gray-600",
    dot: "bg-gray-400",
    bar: "rgba(27,120,64,0.25)",
  },
};

// Generic cost-of-inaction figures (same across services by design).
const COST_STATS = [
  { Icon: Clock, value: "15+", label: "Hours Lost Per Week" },
  { Icon: DollarSign, value: "20-30%", label: "Revenue Lost Opportunity" },
  { Icon: Users, value: "2x", label: "Higher Staff Turnover" },
  { Icon: Frown, value: "Lower", label: "Customer Satisfaction" },
];

const ChallengeCard = ({ problem }) => {
  const cfg = IMPACT[problem.impact] || IMPACT["Moderate Impact"];
  const { Icon } = cfg;
  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-6 flex gap-4"
      style={{ borderLeft: `4px solid ${cfg.bar}` }}
    >
      {/* Consistent, static icon (no hover motion) */}
      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-primary" strokeWidth={2} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 mb-1.5">
          <h3 className="text-base md:text-lg font-bold text-gray-900 leading-snug">
            {problem.title}
          </h3>
          {problem.impact && (
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap flex-shrink-0 ${cfg.badge}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
              {problem.impact}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
          {problem.description}
        </p>
      </div>
    </div>
  );
};

const ServiceChallenges = ({ service, image }) => {
  const problems = service.problems || [];
  if (problems.length === 0) return null;

  // Duplicate the list so the vertical auto-scroll loops seamlessly.
  const loop = [...problems, ...problems];
  // Pace the scroll to the amount of content so every card is readable.
  const duration = Math.max(problems.length * 6, 24);

  return (
    <section className="relative pt-20 pb-4 md:py-24 px-6 md:px-8 lg:px-12 bg-white">
      {/* Scoped marquee animation — pauses on hover so readers keep full context */}
      <style>{`
        @keyframes tercesChallengeScroll {
          from { transform: translateY(0); }
          to   { transform: translateY(-50%); }
        }
        .terces-challenge-track {
          animation: tercesChallengeScroll ${duration}s linear infinite;
          will-change: transform;
        }
        .terces-challenge-viewport:hover .terces-challenge-track {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .terces-challenge-track { animation: none; }
        }
      `}</style>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <div className="inline-block px-6 py-2 bg-primary/10 rounded-full mb-4">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">
              Industrial Challenges
            </span>
          </div>
          <h2
            className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 leading-[1.1] mb-6"
            style={{ letterSpacing: "-0.02em" }}
          >
            What You're Missing Out On
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Without the right solution, hidden problems can quietly drain your
            time, money, and customer satisfaction every single day.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          {/* LEFT — sticky cost-of-inaction panel */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:sticky lg:top-[100px]"
          >
            {/* Image with overlay */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <div className="aspect-[16/10]">
                <img
                  src={
                    image ||
                    "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=600&fit=crop"
                  }
                  alt="Everyday operational challenges"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/45 to-gray-900/10" />

              <div className="absolute top-6 left-6 right-6">
                <h3 className="text-white text-xl md:text-2xl font-extrabold leading-tight mb-2">
                  Small Problems Today.
                  <br />
                  Big Losses Tomorrow.
                </h3>
                <p className="text-white/85 text-sm leading-relaxed max-w-sm">
                  These everyday challenges might seem small — but together, they
                  create huge roadblocks to your growth.
                </p>
              </div>

              {/* Don't Miss Out badge (brand green accent) */}
              <div className="absolute bottom-6 left-6 right-6 bg-white rounded-2xl p-4 shadow-xl flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-primary" strokeWidth={2} />
                </div>
                <div>
                  <div className="text-base font-bold text-primary">Don't Miss Out</div>
                  <div className="text-sm text-gray-600">
                    Act now to stay ahead of the competition.
                  </div>
                </div>
              </div>
            </div>

            {/* Real Cost of Inaction */}
            <div className="mt-8">
              <h4 className="text-lg font-bold text-gray-900 mb-4">
                The Real Cost of Inaction
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {COST_STATS.map(({ Icon, value, label }, i) => (
                  <div
                    key={i}
                    className="bg-primary/[0.06] border border-primary/10 rounded-xl px-3 py-4 text-center"
                  >
                    <Icon className="w-5 h-5 text-primary mx-auto mb-2" strokeWidth={2} />
                    <div className="text-lg font-extrabold text-gray-900 leading-none">
                      {value}
                    </div>
                    <div className="text-[11px] text-gray-600 mt-1.5 leading-tight">
                      {label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-start gap-3 bg-primary/[0.06] border border-primary/10 rounded-xl px-4 py-3">
                <Lightbulb className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={2} />
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-primary">
                    Smart businesses solve problems before they grow.
                  </span>{" "}
                  Identify. Prioritize. Improve. Grow.
                </p>
              </div>
            </div>
          </motion.div>

          {/* RIGHT — auto-scrolling challenge cards */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="terces-challenge-viewport relative overflow-hidden"
            style={{
              maxHeight: "600px",
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent 0, #000 6%, #000 94%, transparent 100%)",
              maskImage:
                "linear-gradient(to bottom, transparent 0, #000 6%, #000 94%, transparent 100%)",
            }}
          >
            <div className="terces-challenge-track flex flex-col gap-5">
              {loop.map((problem, i) => (
                <ChallengeCard key={i} problem={problem} />
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA — "Fix Challenges", no download button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-14 bg-primary/[0.06] border border-primary/15 rounded-2xl px-6 py-6 md:px-10 md:py-7 flex flex-col md:flex-row items-center justify-between gap-5"
        >
          <div className="flex items-center gap-4 text-center md:text-left">
            <div className="w-12 h-12 bg-primary rounded-xl items-center justify-center hidden sm:flex flex-shrink-0">
              <Target className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900">
                Ready to turn these challenges into opportunities?
              </h3>
              <p className="text-gray-600 text-sm md:text-base">
                Let's build a solution that works for your business.
              </p>
            </div>
          </div>
          <Link to="/Contact" className="flex-shrink-0">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary hover:bg-primary-dark text-white rounded-full font-bold text-base shadow-lg hover:shadow-xl transition-all"
            >
              Fix Challenges
              <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceChallenges;
